import { consume } from '@lit/context';
import { t } from 'i18next';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { Ref, createRef, ref } from 'lit/directives/ref.js';

import { PrintOptionDto } from '@/api-sdk';
import { FgPersonalisation } from '@/components';
import { iconDelete } from '@/components/icons';
import {
  ConfiguratorContextType,
  PersonalisationType,
  configuratorGlobal,
  personalisation,
} from '@/context';
import { baseStyles, buttonStyles } from '@/styles';
import { PersonalisationTypes } from '@/types';
import { getPersonalisationData } from '@/utils';

import '@/components/fg-print-option-summary';
import '@/components/personalisation/fg-personalisation';

@customElement('fg-print-option')
export class FgPrintOption extends LitElement {
  fgPersonalisationDialogRef: Ref<FgPersonalisation> = createRef();

  @property({ type: String })
  title = '';

  @property({ type: Object })
  option?: PrintOptionDto;

  @state()
  hasColorChoice!: boolean;

  @state()
  personalisationData?: PersonalisationType;

  @consume({ context: configuratorGlobal.context, subscribe: true })
  configuratorContext!: ConfiguratorContextType;

  @consume({ context: personalisation.context, subscribe: true })
  personalisationContext!: PersonalisationType[];

  render() {
    if (!this.option) {
      return null;
    }

    const { code, removePrintOptionQueryString } = this.option;
    const { configurationId } = this.configuratorContext;

    if (!configurationId) {
      this.personalisationData = personalisation.getDataWithCode(
        this.personalisationContext,
        code || undefined,
      );
    }

    const { type, uploadedFileName, textPersonalization } =
      this.personalisationData || {};

    const hasPersonalisation =
      Boolean(uploadedFileName) || Boolean(textPersonalization);

    const personalisationTypes: PersonalisationTypes = {
      text: {
        title: t('labels.text'),
        description: textPersonalization?.text,
        fontFamily: textPersonalization?.font,
      },
      image: {
        title: t('labels.file'),
        logo: uploadedFileName || undefined,
      },
      text_image: {
        title: t('labels.fileAndText'),
        logo: uploadedFileName || undefined,
        description: textPersonalization?.text,
        fontFamily: textPersonalization?.font,
      },
    };

    return html`
      <strong class="print-option-label">${this.title}</strong>

      <div class="print-option">
        <fg-print-option-summary .option=${this.option}>
          ${type && hasPersonalisation
            ? html`
                <p slot="suffix" class="print-option-personalisation-details">
                  <strong>${personalisationTypes[type].title}:</strong>
                  <span>
                    ${personalisationTypes[type]?.logo
                      ? html`
                          ${personalisationTypes[type]?.logo}
                        `
                      : nothing}
                    ${personalisationTypes[type].description
                      ? html`
                          <br />
                          <span
                            style="
                          ${personalisationTypes[type]?.fontFamily
                              ? `font-family:${personalisationTypes[type]?.fontFamily}`
                              : nothing}
                        "
                          >
                            ${personalisationTypes[type].description}
                          </span>
                        `
                      : nothing}
                  </span>
                  -
                  <button
                    class="button-link"
                    @click=${() =>
                      personalisation.providerRemoveEvent(this, code)}
                  >
                    ${t('actions.delete')}
                  </button>
                </p>
              `
            : html`
                <p slot="suffix" class="print-option-supply-personalisation">
                  <strong>${t('labels.logoDelivery')}</strong>
                  -
                  <button
                    class="button-link"
                    @click=${this.openPersonalisationDialog}
                  >
                    ${t('actions.deliverNow')}
                  </button>
                </p>
              `}
        </fg-print-option-summary>

        <button
          class="button-remove-print-option"
          @click="${() =>
            this.removePrintOption(code, removePrintOptionQueryString)}"
          .title=${t('actions.delete') || ''}
        >
          ${iconDelete}
        </button>

        <fg-personalisation
          ${ref(this.fgPersonalisationDialogRef)}
          .printOption=${this.option}
          .hasColorChoice=${this.hasColorChoice}
          @personalisation-completed=${() => this.requestUpdate()}
        >
          <fg-print-option-summary
            slot="print-option-summary"
            .option=${this.option}
          ></fg-print-option-summary>
        </fg-personalisation>
      </div>
    `;
  }

  openPersonalisationDialog() {
    if (!this.option) {
      return;
    }

    this.hasColorChoice = this.option.hasColorChoice;
    this.fgPersonalisationDialogRef.value?.openDialog();
  }

  removePrintOption(
    printOptionCode: PrintOptionDto['code'],
    queryString: PrintOptionDto['removePrintOptionQueryString'],
  ) {
    this.dispatchEvent(
      new CustomEvent('option-selected', {
        composed: true,
        detail: { option: { queryString } },
      }),
    );
    personalisation.providerRemoveEvent(this, printOptionCode);
  }

  async getPersonalisationFromConfiguration(configurationID: string) {
    getPersonalisationData(this, configurationID);
    this.requestUpdate();
  }

  firstUpdated() {
    const { configurationId } = this.configuratorContext;

    if (configurationId && this.option) {
      this.getPersonalisationFromConfiguration(configurationId);
      configuratorGlobal.updateGlobalProviderEvent(this, {
        ...this.configuratorContext,
        configurationId: '',
      });
    }
  }

  static styles = [
    baseStyles,
    buttonStyles,
    css`
      :host {
        display: block;
        margin-bottom: 15px;
      }

      .print-option-label {
        font-size: 1rem;
        display: block;
        margin-bottom: 10px;
      }

      .print-option {
        position: relative;
        padding: 15px 8px;
        border-radius: var(--fg-borderradius, var(--fg-fallback-borderradius));
        border: 2px solid
          var(--fg-config-selected, var(--fg-fallback-config-selected));
        background-color: var(
          --fg-config-selected--light,
          var(--fg-fallback-config-selected--light)
        );
      }

      .print-option-personalisation-details {
        margin-top: 20px;
      }

      .print-option-personalisation-details strong {
        display: block;
      }

      .print-option-supply-personalisation {
        margin-top: 5px;
      }

      .button-remove-print-option {
        color: #fff;
        position: absolute;
        right: -12px;
        top: -12px;
        border-radius: 100px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        background-color: #000;
      }
    `,
  ];
}
